import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { getGroups, addGroup, deleteGroup, updateGroup } from '../../actions/groupActions';
import { getPrograms, addProgram, deleteProgram, updateProgram } from '../../actions/programActions';
import { getPathsMinimal } from '../../actions/pathActions';
import { getQuestions } from '../../actions/questionActions';
import { getSubmissionForms } from '../../actions/submissionFormActions';
import { getTags } from '../../actions/tagActions';
import { getUsers } from '../../actions/userActions';
import { getTermsConditions, getAvailablePrograms, addTermsCondition, deleteTermsCondition, updateTermsCondition } from '../../actions/termsConditionsActions';
import InvitationsTab from './InvitationsTab';
import ManageUsersTab from './ManageUsersTab';
import ImportExportDataTab from './ImportExportDataTab';
import LessonsManagementContainer from '../lessons/LessonsManagementContainer';
import CoursesManagementContainer from '../courses/CoursesManagementContainer';
import PathsManagementContainer from '../paths/PathsManagementContainer';
import QuestionsManagementContainer from '../questions/QuestionsManagementContainer';
import EventsManagementContainer from '../events/EventsManagementContainer';
import SubmissionFormsManagementContainer from '../submission-forms/SubmissionFormsManagementContainer';
import SubmissionsManagementContainer from '../submissions/SubmissionsManagementContainer';
import ReinforcementsManagementContainer from '../reinforcements/ReinforcementsManagementContainer';
import TagsManagementContainer from '../tags/TagsManagementContainer';
import BadgesManagementContainer from '../badges/BadgesManagementContainer';
import GroupForm from './GroupForm';
import ProgramForm from './ProgramForm';
import TermsConditionForm from '../content-management/TermsConditionForm';
import { FaEye, FaEdit, FaTrash } from 'react-icons/fa';
import { X } from 'lucide-react';
import { isAdminRole } from '../../utils/roleHelpers';
import MondaySyncTab from './MondaySyncTab';
import ReferralsTab from './ReferralsTab';
import ChatbotQATab from './ChatbotQATab';
import QuestionBankTab from './QuestionBankTab';

const TABS = ['Manage Users', 'Invitations', 'Programs', 'Terms & Conditions', 'Groups', 'Referrals', 'Import/Export Data', 'Monday Sync', 'Chatbot Q&A'];

const SettingsContainer = ({ 
  user,
  group: { groups, loading: groupsLoading },
  program: { programs, loading: programsLoading },
  path: { minimalPaths, loading: pathsLoading },
  question: { questions, loading: questionsLoading },
  submissionForm: { submissionForms, loading: submissionFormsLoading },
  userState: { users, loading: usersLoading },
  termsConditions: { termsConditions, availablePrograms, loading: termsConditionsLoading },
  tag: { tags, loading: tagsLoading },
  getGroups,
  addGroup,
  deleteGroup,
  updateGroup,
  getPrograms,
  addProgram,
  deleteProgram,
  updateProgram,
  getPathsMinimal,
  getQuestions,
  getSubmissionForms,
  getUsers,
  getTags,
  getTermsConditions,
  getAvailablePrograms,
  addTermsCondition,
  deleteTermsCondition,
  updateTermsCondition,
  initialTab
}) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [showGroupForm, setShowGroupForm] = useState(false);
  const [groupFormMode, setGroupFormMode] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [showProgramForm, setShowProgramForm] = useState(false);
  const [programFormMode, setProgramFormMode] = useState(null);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [programToDelete, setProgramToDelete] = useState(null);
  const [groupToDelete, setGroupToDelete] = useState(null);
  const [showTermsConditionForm, setShowTermsConditionForm] = useState(false);
  const [termsConditionFormMode, setTermsConditionFormMode] = useState(null);
  const [selectedTermsCondition, setSelectedTermsCondition] = useState(null);
  const [termsConditionToDelete, setTermsConditionToDelete] = useState(null);

  // Check if user is admin
  const isAdmin = isAdminRole(user?.role);

  const visibleTabs = TABS;

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  useEffect(() => {
    if (isAdmin) {
      getGroups();
      getPrograms();
      getQuestions();
      getSubmissionForms();
      getUsers();
      getTags();
      getTermsConditions();
      getAvailablePrograms();
    }
  }, [isAdmin, getGroups, getPrograms, getQuestions, getSubmissionForms, getUsers, getTags, getTermsConditions, getAvailablePrograms]);

  // Group handlers
  const handleAddGroup = () => {
    setGroupFormMode('new');
    setSelectedGroup(null);
    setShowGroupForm(true);
  };
  const handleViewGroup = (group) => {
    setGroupFormMode('view');
    setSelectedGroup(group);
    setShowGroupForm(true);
  };
  const handleEditGroup = (group) => {
    setGroupFormMode('edit');
    setSelectedGroup(group);
    setShowGroupForm(true);
  };
  const handleGroupFormClose = () => {
    setShowGroupForm(false);
    setSelectedGroup(null);
    setGroupFormMode(null);
  };
  const handleGroupFormSubmit = async (data) => {
    try {
      if (groupFormMode === 'edit') {
        await updateGroup(selectedGroup._id, data);
      } else if (groupFormMode === 'new') {
        await addGroup(data);
      }
      handleGroupFormClose();
    } catch (error) {
      // Error will be handled by the action and shown in GroupForm
      throw error;
    }
  };

  // Program handlers
  const handleAddProgram = () => {
    getPathsMinimal();
    setProgramFormMode('new');
    setSelectedProgram(null);
    setShowProgramForm(true);
  };
  const handleViewProgram = (program) => {
    setProgramFormMode('view');
    setSelectedProgram(program);
    setShowProgramForm(true);
  };
  const handleEditProgram = (program) => {
    getPathsMinimal();
    setProgramFormMode('edit');
    setSelectedProgram(program);
    setShowProgramForm(true);
  };
  const handleProgramFormClose = () => {
    setShowProgramForm(false);
    setSelectedProgram(null);
    setProgramFormMode(null);
  };
  const handleProgramFormSubmit = (data) => {
    if (programFormMode === 'edit') {
      updateProgram(selectedProgram._id, data);
    } else if (programFormMode === 'new') {
      addProgram(data);
    }
    handleProgramFormClose();
  };

  // Terms & Conditions handlers
  const handleAddTermsCondition = () => {
    getAvailablePrograms(); // Refresh available programs
    setTermsConditionFormMode('new');
    setSelectedTermsCondition(null);
    setShowTermsConditionForm(true);
  };
  const handleViewTermsCondition = (termsCondition) => {
    setTermsConditionFormMode('view');
    setSelectedTermsCondition(termsCondition);
    setShowTermsConditionForm(true);
  };
  const handleEditTermsCondition = (termsCondition) => {
    getAvailablePrograms(); // Refresh available programs
    setTermsConditionFormMode('edit');
    setSelectedTermsCondition(termsCondition);
    setShowTermsConditionForm(true);
  };
  const handleTermsConditionFormClose = () => {
    setShowTermsConditionForm(false);
    setSelectedTermsCondition(null);
    setTermsConditionFormMode(null);
  };

  // Get appropriate programs list based on mode
  const getAvailableProgramsForMode = () => {
    if (!availablePrograms) return [];
    
    if (termsConditionFormMode === 'new') {
      // New mode: only truly available programs
      return availablePrograms.availablePrograms || [];
    } else if (termsConditionFormMode === 'edit' && selectedTermsCondition) {
      // Edit mode: available programs + currently assigned program (if any)
      const available = availablePrograms.availablePrograms || [];
      const currentProgramId = selectedTermsCondition.program?._id;
      
      if (currentProgramId) {
        // Find the currently assigned program from allPrograms
        const currentProgram = (availablePrograms.allPrograms || []).find(
          p => p._id === currentProgramId
        );
        
        // If current program exists and is not already in available list, add it
        if (currentProgram && !available.find(p => p._id === currentProgramId)) {
          return [currentProgram, ...available];
        }
      }
      
      return available;
    } else {
      // View mode: all programs (to show current assignment)
      return availablePrograms.allPrograms || [];
    }
  };
  const handleTermsConditionFormSubmit = (data) => {
    if (termsConditionFormMode === 'edit') {
      updateTermsCondition(selectedTermsCondition._id, data);
    } else if (termsConditionFormMode === 'new') {
      addTermsCondition(data);
    }
    // Refresh available programs after submission
    setTimeout(() => {
      getAvailablePrograms();
    }, 500);
    handleTermsConditionFormClose();
  };

  const handleDeleteClick = (item) => {
    if (activeTab === 'Programs') {
      setProgramToDelete(item);
    } else if (activeTab === 'Groups') {
      setGroupToDelete(item);
    } else if (activeTab === 'Terms & Conditions') {
      setTermsConditionToDelete(item);
    }
    setShowDeleteConfirmation(true);
  };

  const handleConfirmDelete = () => {
    if (activeTab === 'Programs' && programToDelete) {
      deleteProgram(programToDelete._id);
      setProgramToDelete(null);
    } else if (activeTab === 'Groups' && groupToDelete) {
      deleteGroup(groupToDelete._id);
      setGroupToDelete(null);
    } else if (activeTab === 'Terms & Conditions' && termsConditionToDelete) {
      deleteTermsCondition(termsConditionToDelete._id);
      setTermsConditionToDelete(null);
      // Refresh available programs after deletion
      setTimeout(() => {
        getAvailablePrograms();
      }, 500);
    }
    setShowDeleteConfirmation(false);
  };

  const handleCancelDelete = () => {
    setProgramToDelete(null);
    setGroupToDelete(null);
    setTermsConditionToDelete(null);
    setShowDeleteConfirmation(false);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'Manage Users':
        return <ManageUsersTab />;
      case 'Invitations':
        return <InvitationsTab />;
      case 'Programs':
        return (
          <>
            <ProgramForm
              mode={showProgramForm ? programFormMode : 'list'}
              program={selectedProgram}
              programs={programs}
              paths={minimalPaths || []}
              groups={groups || []}
              questions={questions || []}
              submissionForms={submissionForms || []}
              tags={tags || []}
              onEdit={(program) => {
                getPathsMinimal();
                setProgramFormMode(program ? 'edit' : 'new');
                setSelectedProgram(program);
                setShowProgramForm(true);
              }}
              onDelete={handleDeleteClick}
              onClose={handleProgramFormClose}
              onSubmit={handleProgramFormSubmit}
            />
          </>
        );
      case 'Terms & Conditions':
        return (
          <>
            <TermsConditionForm
              mode={showTermsConditionForm ? termsConditionFormMode : 'list'}
              termsCondition={selectedTermsCondition}
              termsConditions={termsConditions || []}
              availablePrograms={getAvailableProgramsForMode()}
              onView={handleViewTermsCondition}
              onEdit={(termsCondition) => {
                setTermsConditionFormMode(termsCondition ? 'edit' : 'new');
                setSelectedTermsCondition(termsCondition);
                setShowTermsConditionForm(true);
              }}
              onDelete={handleDeleteClick}
              onClose={handleTermsConditionFormClose}
              onSubmit={handleTermsConditionFormSubmit}
            />
          </>
        );
      case 'Groups':
        return (
          <>
            <GroupForm
              mode={showGroupForm ? groupFormMode : 'list'}
              group={selectedGroup}
              groups={groups}
              users={users || []}
              programs={programs || []}
              onEdit={(group) => {
                setGroupFormMode(group ? 'edit' : 'new');
                setSelectedGroup(group);
                setShowGroupForm(true);
              }}
              onDelete={handleDeleteClick}
              onClose={handleGroupFormClose}
              onSubmit={handleGroupFormSubmit}
            />
          </>
        );
      case 'Referrals':
        return <ReferralsTab />;
      case 'Import/Export Data':
        return <ImportExportDataTab />;
      case 'Monday Sync':
        return <MondaySyncTab />;
      case 'Chatbot Q&A':
        return <ChatbotQATab />;
      // Content Management tabs
      case 'Lessons':
        return <LessonsManagementContainer hideSidebar />;
      case 'Courses':
        return <CoursesManagementContainer hideSidebar />;
      case 'Paths':
        return <PathsManagementContainer hideSidebar />;
      case 'Question Bank':
        return <QuestionBankTab />;
      case 'Questions':
        return <QuestionsManagementContainer hideSidebar />;
      case 'Events & Info':
        return <EventsManagementContainer hideSidebar />;
      case 'Submission Forms':
        return <SubmissionFormsManagementContainer hideSidebar />;
      case 'Submissions':
        return <SubmissionsManagementContainer hideSidebar />;
      case 'Reinforcements':
        return <ReinforcementsManagementContainer hideSidebar />;
      case 'Tags':
        return <TagsManagementContainer hideSidebar />;
      case 'Badges':
        return <BadgesManagementContainer hideSidebar />;
      default:
        return null;
    }
  };

  return (
    <div className="flex-1 min-w-0">
      <div className="p-4 sm:p-6">
        {renderTabContent()}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-card rounded-lg shadow-lg w-full max-w-md p-6 border border-border">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-foreground">
                Confirm Delete
              </h3>
              <button
                onClick={handleCancelDelete}
                className="text-foreground/40 hover:text-foreground/60 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-foreground/60 mb-6">
              Are you sure you want to delete this {
                activeTab === 'Programs' ? 'program' : 
                activeTab === 'Terms & Conditions' ? 'terms & conditions' :
                'group'
              }? This action cannot be undone.
            </p>
            <div className="flex flex-col-reverse sm:flex-row gap-3 justify-end">
              <button
                onClick={handleCancelDelete}
                className="w-full sm:w-auto px-4 py-2 border border-[#1086a4] rounded-md text-[#1086a4] font-medium hover:bg-[#f9fafb] focus:outline-none focus:ring-2 focus:ring-[#1086a4] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="w-full sm:w-auto px-4 py-2 bg-red-500 text-white rounded-md font-medium hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = state => ({
  user: state.auth.user,
  group: state.group,
  program: state.program,
  path: state.path,
  question: state.question,
  submissionForm: state.submissionForm,
  userState: state.user,
  termsConditions: state.termsConditions,
  tag: state.tag
});

export default connect(mapStateToProps, {
  getGroups,
  addGroup,
  deleteGroup,
  updateGroup,
  getPrograms,
  addProgram,
  deleteProgram,
  updateProgram,
  getPathsMinimal,
  getQuestions,
  getSubmissionForms,
  getUsers,
  getTags,
  getTermsConditions,
  getAvailablePrograms,
  addTermsCondition,
  deleteTermsCondition,
  updateTermsCondition
})(SettingsContainer);
